import { styled } from '@linaria/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useOrganizationDocuments } from '../../api/agents-client/agent-client.hooks';
import { useGoogleStatus } from '../../api/google-integrations-client/google-integrations-client.hooks';
import { useOrganizationId } from '../../helpers/auth-helpers/auth.hooks';
import { useGlobalStore } from '../../store/global-store/global-store';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { Loader } from '../../ui-library/loader/loader';
import { DashboardInfoCard } from '../dashboards/components/dashboard-info-card';
import { isPreviewableDocType } from '../flora/components/flora-document/flora-document.helpers';
import { getPlainTextContent } from '../flora/components/flora-message/flora-message.helpers';
import type {
  ArtifactSpec,
  ChatHistory,
  Document,
  Message,
  TokenUsage,
  ToolApproval,
  ToolApprovalDecision,
} from '../flora/flora.type';
import './artifacts/register-artifacts';
import { AgentHistoryPanel } from './components/agent-history-panel';
import { AgentSearchModal } from './components/agent-search-modal';
import {
  FLORA_SETTINGS_PATHS,
  getFloraConnectionPath,
  isFloraSettingsPath,
} from './components/agent-settings/agent-settings.constants';
import { FloraConnectionId } from './components/agent-settings/agent-settings.type';
import { GrantSheetAccessModal } from './components/grant-sheet-access-modal';
import './flora-agent-dark-theme.css';
import { computeDocumentRevisions, shouldReuseDocumentLinks } from './flora-agent.helpers';
import type { AgentAttachmentsBinding } from '../flora/components/chat/use-agent-attachments';
import { useFloraAgent, useFloraDarkMode, useRailAutoOpenOnLivePublish } from './flora-agent.hooks';
import { extractGoogleFileLinks, type GoogleFileLink, useSheetAccessGate } from './hooks/use-sheet-access-gate';

export interface FloraAgentOutletContext {
  messages: Message[];
  questions: Message[];
  threadId: string;
  chatId: string | undefined;
  chatHistories: ChatHistory[];
  question: string;
  setQuestion: (q: string) => void;
  submitQuestion: (msg?: string, displayMsg?: string) => void;
  attachments: AgentAttachmentsBinding;
  isActiveQuestion: boolean;
  isWaitingForFirstResponse: boolean;
  loadingMessage: string;
  tokenUsage: TokenUsage | null;
  latestTurnIsLive: boolean;
  handleNewChat: () => void;
  handleHistoryClick: (id: string) => void;
  refreshChatHistories: () => void;
  artifactPanelOpen: boolean;
  onToggleArtifactPanel: () => void;
  allArtifacts: ArtifactSpec[];
  documentPanelOpen: boolean;
  onOpenGoogleFile: (fileId: string) => void;
  onSelectGoogleFile: (fileId: string) => void;
  selectedGoogleFileId: string | undefined;
  onCloseFilesPanel: () => void;
  allDocuments: GoogleFileLink[];
  allDocumentRevisions: Record<string, number>;
  pagePanelOpen: boolean;
  onTogglePagePanel: () => void;
  hasPages: boolean;
  selectedPageId: string | undefined;
  docPreviewPanelOpen: boolean;
  onPreviewDocument: (documentId: string) => void;
  onSelectPreviewDoc: (documentId: string) => void;
  previewableDocs: Document[];
  selectedPreviewDocId: string | undefined;
  isDarkMode: boolean;
  handleAnswerSubmit: (jobId: string, answers: Record<string, string | string[]>) => void;
  stopAgent: () => void;
  handleToolApproval: (approvalDecision: ToolApprovalDecision, toolApproval: ToolApproval) => void;
}

export function FloraAgentLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isOnSettingsRoute = isFloraSettingsPath(location.pathname);
  const useAI = useGlobalStore((state) => state.useAI);
  const useAIResolved = useGlobalStore((state) => state.useAIResolved);
  const organizationId = useOrganizationId();
  const isGlobalStateInitialized = useGlobalStore((state) => state.initialized);
  const useAIResolvedOrganizationId = useGlobalStore((state) => state.useAIResolvedOrganizationId);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useFloraDarkMode();

  useDocumentTitle('Flora - Bloomfilter');

  const {
    messages,
    questions,
    threadId,
    chatId,
    chatHistories,
    question,
    isActiveQuestion,
    isWaitingForFirstResponse,
    loadingMessage,
    tokenUsage,
    latestTurnIsLive,
    submitQuestion,
    attachments,
    setQuestion,
    handleNewChat,
    handleDeleteHistory,
    handleEditHistory,
    handleHistoryClick,
    handleAnswerSubmit,
    stopAgent,
    handleToolApproval,
    fetchChatHistories,
  } = useFloraAgent();

  const refreshChatHistories = useCallback((): void => {
    fetchChatHistories();
  }, [fetchChatHistories]);

  const { guardedSubmit, isModalOpen, modalStatus, fileType, grantAccess, sendAnyway, cancel } = useSheetAccessGate({
    submitQuestion,
    question,
  });

  // The Documents page hosts both Flora-generated documents and the connected
  // user's Google Drive files, so surface the tab when either applies — an org
  // with Flora documents but no Google connection must still be able to reach
  // them. `useOrganizationDocuments` collapses both "still loading" and "the
  // request failed" into an empty array, so fail open on those (rather than on
  // `documents.length > 0` alone) — otherwise the nav item would flicker away
  // during the initial load, or disappear entirely on a transient error, for an
  // org with real Flora documents and no Google connection.
  const { data: googleStatus } = useGoogleStatus();
  const {
    documents: organizationDocuments,
    isLoading: isLoadingOrganizationDocuments,
    error: organizationDocumentsError,
  } = useOrganizationDocuments();
  const showDocuments =
    Boolean(googleStatus?.connected) ||
    organizationDocuments.length > 0 ||
    isLoadingOrganizationDocuments ||
    Boolean(organizationDocumentsError);

  // The right rail hosts one panel at a time: artifacts, documents, or a
  // published page. They share the same slider so only one can be open, keeping
  // the layout single-column.
  const [openPanel, setOpenPanel] = useState<'artifacts' | 'documents' | 'page' | 'doc-preview' | null>(null);
  const artifactPanelOpen = openPanel === 'artifacts';
  const documentPanelOpen = openPanel === 'documents';
  const pagePanelOpen = openPanel === 'page';
  const docPreviewPanelOpen = openPanel === 'doc-preview';
  // The specific document the preview rail should focus, set when a document
  // card or a Files-menu entry is clicked. Undefined falls back to the newest
  // previewable doc.
  const [selectedPreviewDocId, setSelectedPreviewDocId] = useState<string | undefined>(undefined);
  // The Google file the documents rail should show, set from the Files menu.
  // Undefined (e.g. the rail auto-opened for a newly written file) falls back
  // to the viewer's own default.
  const [selectedGoogleFileId, setSelectedGoogleFileId] = useState<string | undefined>(undefined);

  // Page artifacts drive a dedicated full-height rail viewer (like documents),
  // not the collapsible chart cards — so keep them out of `allArtifacts` to
  // preserve the charts panel's auto-open/toggle behavior.
  const allSpecs = useMemo<ArtifactSpec[]>(() => messages.flatMap((m) => m.artifacts || []), [messages]);
  const allArtifacts = useMemo<ArtifactSpec[]>(() => allSpecs.filter((spec) => spec.type !== 'page'), [allSpecs]);
  const pageArtifacts = useMemo<ArtifactSpec[]>(() => allSpecs.filter((spec) => spec.type === 'page'), [allSpecs]);
  // The rail shows the most recently published page in the conversation. Resolve
  // to a valid string page id or nothing: a malformed page artifact (missing a
  // string `page_id`) must not open a rail that can only render a blank frame —
  // `hasPages` and the auto-open both key off this resolved id, not the raw
  // artifact count.
  const selectedPageId = useMemo<string | undefined>(() => {
    // Search newest-to-oldest for the first valid page id: if the most recent
    // page artifact is malformed (missing a string `page_id`), fall back to an
    // earlier valid one rather than dropping the rail entirely.
    for (let index = pageArtifacts.length - 1; index >= 0; index -= 1) {
      const candidate = pageArtifacts[index]?.data.page_id;
      if (typeof candidate === 'string' && candidate.length > 0) {
        return candidate;
      }
    }
    return undefined;
  }, [pageArtifacts]);
  const hasPages = selectedPageId !== undefined;

  // Flora documents attached to the conversation that the preview rail can show
  // (Word/Excel as a PDF, plain-text types from their text). Deduped by document id (freshest occurrence wins)
  // and returned newest-first so the panel defaults to the most recent doc.
  const previewableDocs = useMemo<Document[]>(() => {
    const byId = new Map<string, Document>();
    for (const message of messages) {
      for (const doc of message.documents ?? []) {
        if (isPreviewableDocType(doc.doc_type)) {
          // Delete before re-inserting: `Map.set` on an existing key updates the
          // value but keeps the original insertion position, so a document Flora
          // revised later in the conversation would keep its first-seen slot and
          // the `.reverse()` below would not surface it first.
          byId.delete(doc.document_id);
          byId.set(doc.document_id, doc);
        }
      }
    }
    return Array.from(byId.values()).reverse();
  }, [messages]);

  // Google Docs/Sheets links referenced anywhere in the conversation, deduped by
  // file id, surfaced as read-only inline previews in the documents panel.
  const completedToolCallCount = useMemo(
    () =>
      messages.reduce(
        (total, message) =>
          total + (message.toolCalls ?? []).filter((toolCall) => toolCall.status !== 'pending').length,
        0,
      ),
    [messages],
  );
  const lastDocumentsRef = useRef<GoogleFileLink[]>([]);
  const lastDocumentScanToolCallCountRef = useRef(0);
  const lastDocumentConversationKeyRef = useRef<string | null>(null);
  const documentConversationKey = `${chatId ?? ''}:${threadId}`;
  const allDocuments = useMemo<GoogleFileLink[]>(() => {
    // A streaming turn replaces `messages` on every text chunk. Keep the last
    // settled result for those chunks, but rescan after each completed or failed
    // tool call: that is when the agent may have received a new Google file URL.
    // The completed turn still gets a final scan below.
    if (
      shouldReuseDocumentLinks({
        isActiveQuestion,
        conversationKey: documentConversationKey,
        cachedConversationKey: lastDocumentConversationKeyRef.current,
        completedToolCallCount,
        cachedCompletedToolCallCount: lastDocumentScanToolCallCountRef.current,
      })
    ) {
      return lastDocumentsRef.current;
    }
    const combinedText = [...questions, ...messages]
      .map((message) => (typeof message.content === 'string' ? message.content : getPlainTextContent(message)))
      .join('\n');
    const documents = extractGoogleFileLinks(combinedText);
    lastDocumentsRef.current = documents;
    lastDocumentScanToolCallCountRef.current = completedToolCallCount;
    lastDocumentConversationKeyRef.current = documentConversationKey;
    return documents;
  }, [questions, messages, isActiveQuestion, completedToolCallCount, documentConversationKey]);

  // Per-file revision counts: bump whenever a Google editing tool call touches a
  // referenced file, so the viewer can remount that file's iframe and drop
  // Google's cached (pre-edit) preview. `messages` is intentionally omitted from
  // the deps: revision counts change only when a tool call settles
  // (completedToolCallCount) or the conversation switches, never on the plain
  // text chunks that churn `messages` mid-stream — so this recomputes exactly
  // when a rescan is warranted while staying a pure render (no cache refs to
  // mutate). Reading the current-render `messages` is safe because it is only
  // consulted on the renders where one of the listed keys actually changed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `messages` is read but must not trigger recompute on stream chunks; see comment above.
  const allDocumentRevisions = useMemo<Record<string, number>>(
    () =>
      computeDocumentRevisions(
        messages,
        allDocuments.map((link) => link.fileId),
      ),
    [allDocuments, completedToolCallCount, documentConversationKey],
  );

  // Auto-open the artifacts panel when artifacts arrive, but never steal the rail
  // from a user who has intentionally opened a panel of their own (e.g. while an
  // artifact streams in incrementally during deep research). `doc-preview` is in
  // this list for the same reason `documents` is: it is a deliberate user
  // action, and re-opening it re-runs the whole conversion because the preview
  // query is deliberately uncached.
  useEffect(() => {
    if (allArtifacts.length > 0) {
      setOpenPanel((prev) => (prev === 'documents' || prev === 'doc-preview' ? prev : 'artifacts'));
    }
  }, [allArtifacts.length]);

  // Surface the Files rail only when Flora writes a new Google Doc/Sheet/Slides
  // during the live turn — never when merely opening a conversation that already
  // references one. `latestTurnIsLive` is true only for a turn that just streamed
  // in this session's thread, so a history load (where it is false) leaves the
  // rail closed. Preserve a panel the user has already opened.
  const previousDocumentCountRef = useRef(0);
  const documentCountConversationKeyRef = useRef(documentConversationKey);
  useEffect(() => {
    // FloraAgentLayout stays mounted across chat/thread switches, so the count
    // baseline would otherwise leak between conversations — a new conversation
    // starting with fewer documents could make its first generated doc look like
    // a removal and skip opening the rail. Rebaseline (without opening) whenever
    // the conversation changes.
    if (documentCountConversationKeyRef.current !== documentConversationKey) {
      documentCountConversationKeyRef.current = documentConversationKey;
      previousDocumentCountRef.current = allDocuments.length;
      return;
    }

    const documentAdded = allDocuments.length > previousDocumentCountRef.current;
    previousDocumentCountRef.current = allDocuments.length;

    if (documentAdded && latestTurnIsLive) {
      // Focus the file Flora just wrote: links keep first-appearance order, so
      // the newest is last, and a stale Files-menu choice must not win.
      setSelectedGoogleFileId(allDocuments[allDocuments.length - 1]?.fileId);
      setOpenPanel((prev) => prev ?? 'documents');
    }
  }, [allDocuments, latestTurnIsLive, documentConversationKey]);

  // Auto-open the page rail only when Flora publishes a page during the live
  // turn — never on a history load that already contains one. `prev ?? 'page'`
  // preserves a panel the user has already opened. The blank-frame guard lives
  // downstream (the rail only renders when `hasPages` resolves a valid id), so
  // driving this off the raw page-artifact count is safe.
  const openPageRail = useCallback(() => setOpenPanel((prev) => prev ?? 'page'), []);
  useRailAutoOpenOnLivePublish(pageArtifacts.length, documentConversationKey, latestTurnIsLive, openPageRail);

  // Open the preview rail focused on a specific document (a document-card
  // click). Always opens rather than toggles, so clicking a second card while
  // the rail is open switches to that document instead of closing it.
  const handlePreviewDocument = useCallback((documentId: string) => {
    setSelectedPreviewDocId(documentId);
    setOpenPanel('doc-preview');
  }, []);

  // Change which document the (already-open) preview rail shows, e.g. from its
  // in-panel document switcher. Selection is fully owned here so a re-click of
  // the same card after a switcher change still re-focuses it.
  const handleSelectPreviewDoc = useCallback((documentId: string) => {
    setSelectedPreviewDocId(documentId);
  }, []);

  // Open the Google documents rail on a file chosen from the header Files menu.
  const handleOpenGoogleFile = useCallback((fileId: string) => {
    setSelectedGoogleFileId(fileId);
    setOpenPanel('documents');
  }, []);

  // Close whichever file panel (Flora preview or Google) the Files menu opened,
  // leaving any other rail (artifacts, page) untouched.
  const handleCloseFilesPanel = useCallback(() => {
    setOpenPanel((prev) => (prev === 'documents' || prev === 'doc-preview' ? null : prev));
  }, []);

  const handleNewChatWithReset = useCallback(() => {
    setOpenPanel(null);
    setSelectedPreviewDocId(undefined);
    setSelectedGoogleFileId(undefined);
    handleNewChat();
  }, [handleNewChat]);

  // Cmd+K to open search, Cmd+Shift+O for new chat
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'o') {
        e.preventDefault();
        handleNewChatWithReset();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleNewChatWithReset]);

  // Switching conversations closes the rail. The auto-open effects only reopen
  // it on a live publish, so a history load that already contains a page never
  // silently opens the rail (which would mint a single-view signed URL and load
  // the untrusted iframe with no user intent).
  const handleHistoryClickWithReset = useCallback(
    (id: string) => {
      setOpenPanel(null);
      setSelectedPreviewDocId(undefined);
      setSelectedGoogleFileId(undefined);
      handleHistoryClick(id);
    },
    [handleHistoryClick],
  );

  const handleHistoryClickFromSidebar = useCallback(
    (id: string) => {
      handleHistoryClickWithReset(id);
    },
    [handleHistoryClickWithReset],
  );

  const handleSearchResultClick = useCallback(
    (chatId: string) => {
      handleHistoryClickWithReset(chatId);
    },
    [handleHistoryClickWithReset],
  );

  const handleCommandsClick = useCallback(() => {
    navigate('/application/flora-agent/commands');
  }, [navigate]);

  const handleDocumentsClick = useCallback(() => {
    navigate('/application/flora-agent/documents');
  }, [navigate]);

  const handleAutomationsClick = useCallback(() => {
    navigate('/application/flora-agent/automations');
  }, [navigate]);

  const handlePagesClick = useCallback(() => {
    navigate('/application/flora-agent/pages');
  }, [navigate]);

  const handleSettingsClick = useCallback(() => {
    navigate(FLORA_SETTINGS_PATHS.root);
  }, [navigate]);

  const handlePreferencesClick = useCallback(() => {
    navigate(FLORA_SETTINGS_PATHS.profile);
  }, [navigate]);

  // On a cold load useAI holds its pre-check default (false) until the
  // canUseAI query settles — deciding access off that default would flash
  // "Access Denied" (and bounce redirects) at users who do have access. The
  // result must be for this organization; a completed request for a prior
  // scope cannot authorize or deny the current one. Exception: an initialized
  // session with no organization can never run the check, so fall through to
  // the denied card rather than spinning forever.
  const accessCheckCanResolve = !isGlobalStateInitialized || !!organizationId;
  const isCurrentOrganizationAccessResolved = useAIResolved && useAIResolvedOrganizationId === organizationId;
  if (!isCurrentOrganizationAccessResolved && accessCheckCanResolve) {
    return (
      <CenteredNotice>
        <Loader />
      </CenteredNotice>
    );
  }

  if (!useAI) {
    return (
      <CenteredNotice>
        <DashboardInfoCard
          iconName="info"
          title="Access Denied"
          objectName="Flora"
          description="You do not have access to Flora. Talk to your Bloomfilter Representative about enabling AI in your organization."
        />
      </CenteredNotice>
    );
  }

  const outletContext: FloraAgentOutletContext = {
    messages,
    questions,
    threadId,
    chatId,
    chatHistories,
    question,
    setQuestion,
    submitQuestion: guardedSubmit,
    attachments,
    isActiveQuestion,
    isWaitingForFirstResponse,
    loadingMessage,
    tokenUsage,
    latestTurnIsLive,
    handleNewChat: handleNewChatWithReset,
    handleHistoryClick: handleHistoryClickWithReset,
    refreshChatHistories,
    artifactPanelOpen,
    onToggleArtifactPanel: () => setOpenPanel((prev) => (prev === 'artifacts' ? null : 'artifacts')),
    allArtifacts,
    documentPanelOpen,
    onOpenGoogleFile: handleOpenGoogleFile,
    onSelectGoogleFile: setSelectedGoogleFileId,
    selectedGoogleFileId,
    onCloseFilesPanel: handleCloseFilesPanel,
    allDocuments,
    allDocumentRevisions,
    pagePanelOpen,
    onTogglePagePanel: () => setOpenPanel((prev) => (prev === 'page' ? null : 'page')),
    hasPages,
    selectedPageId,
    docPreviewPanelOpen,
    onPreviewDocument: handlePreviewDocument,
    onSelectPreviewDoc: handleSelectPreviewDoc,
    previewableDocs,
    selectedPreviewDocId,
    isDarkMode,
    handleAnswerSubmit,
    stopAgent,
    handleToolApproval,
  };

  return (
    <Shell data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Settings bring their own menu and a "Back to chat" link, so the history
          panel would only add a second sidebar there. */}
      {!isOnSettingsRoute && (
        <AgentHistoryPanel
          chatHistories={chatHistories}
          activeChatId={chatId}
          isOpen={historyOpen}
          isDarkMode={isDarkMode}
          onToggle={() => setHistoryOpen(!historyOpen)}
          onHistoryClick={handleHistoryClickFromSidebar}
          onNewChat={handleNewChatWithReset}
          onDelete={handleDeleteHistory}
          onEdit={handleEditHistory}
          onSearch={() => setSearchOpen(true)}
          onCommands={handleCommandsClick}
          onDocuments={handleDocumentsClick}
          onAutomations={handleAutomationsClick}
          onPages={handlePagesClick}
          onSettings={handleSettingsClick}
          onPreferences={handlePreferencesClick}
          onToggleDarkMode={toggleDarkMode}
          showDocuments={showDocuments}
        />
      )}

      <MainArea style={{ '--content-max-width': historyOpen ? '960px' : '1200px' } as React.CSSProperties}>
        <Outlet context={outletContext} />
      </MainArea>
      <AgentSearchModal
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        chatHistories={chatHistories}
        onResultClick={handleSearchResultClick}
      />
      <GrantSheetAccessModal
        opened={isModalOpen}
        status={modalStatus}
        fileType={fileType}
        onGrantAccess={grantAccess}
        onSendAnyway={sendAnyway}
        onCancel={cancel}
        onConnectGoogle={() => {
          cancel();
          navigate(getFloraConnectionPath(FloraConnectionId.Google));
        }}
      />
    </Shell>
  );
}

const Shell = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
  background: var(--base-white);
`;

const MainArea = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const CenteredNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
`;
